import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Upload, message, Avatar, Row, Col, Card, Divider, Typography, Select, Checkbox, Space, Tag, Spin } from 'antd';
import { 
  UserOutlined, 
  GlobalOutlined,
  PhoneOutlined, 
  HomeOutlined, 
  BankOutlined,
  DollarOutlined,
  InboxOutlined 
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import './index.css';
import company from '../../services/company';
import PageLayout from '../../components/layout/Layout';
import env from '../../env';
import states from '../../services/states';
import lineOfBusiness from '../../services/lineOfBusiness';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddCompanyPage = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [companyImage, setCompanyImage] = useState(null);
  const [companyImageUrl, setCompanyImageUrl] = useState(null);
  const [companyImageName, setCompanyImageName] = useState(null);
  const [files, setFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [lobList, setLobList] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedLobs, setSelectedLobs] = useState([]);
  const [stateFilter, setStateFilter] = useState('');
  const [lobFilter, setLobFilter] = useState('');
  const [statesLoading, setStatesLoading] = useState(false);
  const [lobLoading, setLobLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const applyCompanyCatalogDefaults = (formData) => {
    formData.set('is_searchable_lob', '1');
    formData.set('is_searchable_ao', '0');
    formData.set('sponsored', '0');
  };

  const modules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link'],
      ['clean']                                         
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  useEffect(() => {
    if (id) {
      fetchCompanyData();
    }
    fetchStates();
    fetchLineOfBusiness();
  }, [id]);

  const fetchCompanyData = async () => {
    try {
      setDataLoading(true);
      const response = await company.getById(id);
      const companyData = response.data;
      console.log(companyData);
      // Set form values
      form.setFieldsValue({
        name: companyData.name,
        phone: companyData.phone,
        website: companyData.website,
        contact: companyData.contact,
        address: companyData.address,
        general_info: companyData.general_info,
        commission: companyData.commission,
        limits_of_coverage: companyData.limits_of_coverage,
        underwriting: companyData.underwriting,
        states: companyData.states?.map(state => state.id) || [],
        line_of_businesses: companyData.line_of_businesses?.map(lob => lob.id) || []
      });

      // Set the selected states and lobs in state using IDs
      setSelectedStates(companyData.states?.map(state => state.id) || []);
      setSelectedLobs(companyData.line_of_businesses?.map(lob => lob.id) || []);

      // Set logo if exists
      if (companyData.logo) {
        setCompanyImageUrl(`${env.BACKEND_STORAGE_URL}/${companyData.logo}`);
        setCompanyImageName(companyData.logo);
      }

      // Set files if exist
      if (companyData.files) {
        const fileList = companyData.files.map(file => ({
          uid: file.id,
          name: file.file_name,
          status: 'done',
          url: `${env.BACKEND_STORAGE_URL}/${file.file_path}`
        }));
        setFiles(fileList);
      }

    } catch (error) {
      message.error('Failed to fetch company data');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const response = await states.get();
      setStatesList(response.map(state => ({
        value: state.id,
        label: state.name
      })));
    } catch (error) {
      console.error('Error fetching states:', error);
      message.error('Failed to fetch states');
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchLineOfBusiness = async () => {
    try {
      setLobLoading(true);
      const response = await lineOfBusiness.get();
      setLobList(response.map(lob => ({
        value: lob.id,
        label: lob.name
      })));
    } catch (error) {
      console.error('Error fetching line of business:', error);
      message.error('Failed to fetch line of business options');
    } finally {
      setLobLoading(false);
    }
  };

  const handleLogoChange = (info) => {
    const file = info.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCompanyImageUrl(e.target.result); // For preview
        setCompanyImage(file); // Store the actual file for upload
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFilesChange = ({ fileList }) => {
    // Track deleted files when a file is removed
    const removedFile = files.find(f => !fileList.find(newF => newF.uid === f.uid));
    if (removedFile && removedFile.uid) {
      setDeletedFiles(prev => [...prev, removedFile.uid]);
    }
    setFiles(fileList);
  };

  const logoProps = {
    name: 'logo',
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file) => {
      return false;
    },
    onChange: handleLogoChange,
  };

  const filesProps = {
    name: 'files',
    multiple: true,
    accept: '.pdf,.doc,.docx,.xls,.xlsx',
    fileList: files,
    beforeUpload: (file) => {
      return false; // Prevent automatic upload
    },
    onChange: handleFilesChange,
  };

  const filteredStates = statesList.filter(state => 
    state.label.toLowerCase().includes(stateFilter.toLowerCase())
  );

  const filteredLobs = lobList.filter(lob => 
    lob.label.toLowerCase().includes(lobFilter.toLowerCase())
  );

  const handleStateSelect = (state) => {
    const newStates = selectedStates.includes(state.value) 
      ? selectedStates.filter(s => s !== state.value)
      : [...selectedStates, state.value];
    setSelectedStates(newStates);
    form.setFieldsValue({ states: newStates });
  };

  const handleLobSelect = (lob) => {
    const newLobs = selectedLobs.includes(lob.value)
      ? selectedLobs.filter(l => l !== lob.value)
      : [...selectedLobs, lob.value];
    setSelectedLobs(newLobs);
    form.setFieldsValue({ line_of_businesses: newLobs });
  };

  const handleSubmit = async (values) => {
    console.log('Form values:', values);
    try {
      setLoading(true);
      const formData = new FormData();

      // Clean up ReactQuill data
      const cleanHTML = (html) => {
        if (!html || html === '<p><br></p>' || html === '<p></p>') {
          return '';
        }
        return html.replace(/<p><br><\/p>/g, '').trim();
      };

      // Append all text fields
      Object.keys(values).forEach(key => {
        if (key === 'states') {
          // Handle states array - send IDs
          values[key]?.forEach(stateId => {
            formData.append(`states[]`, stateId);
          });
        } else if (key === 'line_of_businesses') {
          // Handle line of businesses array - send IDs
          values[key]?.forEach(lobId => {
            formData.append(`line_of_businesses[]`, lobId);
          });
        } else if (key === 'general_info' || key === 'limits_of_coverage' || key === 'underwriting') {
          // Clean up ReactQuill data before appending
          const cleanedValue = cleanHTML(values[key]);
          if (cleanedValue) {
            formData.append(key, cleanedValue);
          }
        } else if (values[key] !== null && values[key] !== undefined) {
          // Append all other fields, even if empty string
          formData.append(key, values[key]);
        }
      });

      // Append logo if exists
      if (companyImage) {
        formData.append('logo', companyImage);
      }

      // Append deleted file IDs if any
      if (deletedFiles.length > 0) {
        deletedFiles.forEach(fileId => {
          formData.append(`deleted_files[]`, fileId);
        });
      }

      // Append files if they exist
      if (files) {
        files.forEach(file => {
          if (file.originFileObj) {
            formData.append(`files[]`, file.originFileObj);
          }
        });
      }

      // Log formData contents for debugging
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      applyCompanyCatalogDefaults(formData);

      if (id) {
        await company.update(id, formData);
        message.success('Company updated successfully');
        // Invalidate the companies query cache to trigger a refresh
        queryClient.invalidateQueries(['companies']);
        //navigate('/admin/companies');
      } else {
        await company.add(formData);
        message.success('Company added successfully');
        // Invalidate the companies query cache to trigger a refresh
        queryClient.invalidateQueries(['companies']);
        navigate('/admin/companies');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error('An error occurred while saving the company');
      }
    } finally {
      setLoading(false);
    }
  };

  // Add form error handler
  const handleFormError = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    // Don't reset selections on form error
  };

  const handlePreview = () => {
    window.open(`/company/${id}`, '_blank');
  };

  return (
    <PageLayout>
      <div className="company-form-container">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div className="page-header">
              <Title level={2}>{id ? 'Edit Company' : 'Add New Company'}</Title>
              <Text type="secondary">
                {id ? 'Update company information and details' : 'Create a new company profile'}
              </Text>
            </div>
          </Col>
        </Row>

        <Spin spinning={dataLoading}>
        <Row gutter={[24, 24]} >
          <Col xs={24} md={8} lg={6}>
            <Card className="company-media-card">
              <div className="company-logo-section">
                <Title level={5}>Company Logo</Title>
                <Text type="secondary" className="upload-hint">
                  Upload a square company logo or brand image
                </Text>
                <Upload {...logoProps} className="logo-uploader">
                  {companyImageUrl ? (
                    <Avatar
                      src={companyImageUrl}
                      size={200}
                      className="company-avatar"
                    />
                  ) : (
                    <div className="upload-placeholder">
                      <BankOutlined className="upload-icon" />
                      <Text>Click to Upload</Text>
                    </div>
                  )}
                </Upload>
              </div>

              <Divider />

              <div className="company-documents-section">
                <Title level={5}>Company Documents</Title>
                <Text type="secondary" className="upload-hint">
                  Upload relevant company documents and files
                </Text>
                <Upload.Dragger {...filesProps} className="documents-uploader">
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to upload
                  </p>
                  <p className="ant-upload-hint">
                    Supports: PDF, Word, Excel
                  </p>
                </Upload.Dragger>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={16} lg={18}>
            <Card className={`company-details-card ${dataLoading ? 'loading' : ''}`}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onFinishFailed={handleFormError}
                className="company-form"
                preserve={true}
              >
                <div className="form-section">
                  <Title level={5}>Basic Information</Title>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="name"
                        label="Company Name"
                        rules={[{ required: true, message: 'Please enter company name' }]}
                      >
                        <Input prefix={<BankOutlined />} placeholder="Enter company name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="website"
                        label="Website"
                      >
                        <Input prefix={<GlobalOutlined />} placeholder="https://example.com" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="contact"
                        label="Contact Person"
                      >
                        <Input prefix={<UserOutlined />} placeholder="Primary contact name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[{ required: true, message: 'Please enter phone number' }]}
                      >
                        <Input prefix={<PhoneOutlined />} placeholder="Contact phone number" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <Divider />

                <div className="form-section">
                  <Title level={5}>Additional Details</Title>
                  <Form.Item
                    name="address"
                    label="Address"
                  >
                    <TextArea
                      placeholder="Company address"
                      autoSize={{ minRows: 3, maxRows: 5 }}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="states"
                        label="States"
                        rules={[{ required: true, message: 'Please select at least one state' }]}
                      >
                        <div className="selection-container">
                          {statesLoading ? (
                            <div className="loading-container">
                              <Spin size="small" />
                              <span>Loading states...</span>
                            </div>
                          ) : (
                            <>
                              <div className="selected-items">
                                {selectedStates.map(stateId => {
                                  const state = statesList.find(s => s.value === stateId);
                                  return (
                                    <Tag 
                                      key={stateId}
                                      closable
                                      onClose={() => handleStateSelect({ value: stateId })}
                                      className="selected-tag"
                                    >
                                      {state?.label}
                                    </Tag>
                                  );
                                })}
                              </div>
                              <Input
                                placeholder="Search states..."
                                value={stateFilter}
                                onChange={e => setStateFilter(e.target.value)}
                                className="search-input"
                              />
                              <div className="suggestions-list">
                                {filteredStates.map(state => (
                                  <div
                                    key={state.value}
                                    className={`suggestion-item ${selectedStates.includes(state.value) ? 'selected' : ''}`}
                                    onClick={() => handleStateSelect(state)}
                                  >
                                    {state.label}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="line_of_businesses"
                        label="Line of Business"
                        rules={[{ required: true, message: 'Please select at least one business line' }]}
                      >
                        <div className="selection-container">
                          {lobLoading ? (
                            <div className="loading-container">
                              <Spin size="small" />
                              <span>Loading business lines...</span>
                            </div>
                          ) : (
                            <>
                              <div className="selected-items">
                                {selectedLobs.map(lobId => {
                                  const lob = lobList.find(l => l.value === lobId);
                                  return (
                                    <Tag 
                                      key={lobId}
                                      closable
                                      onClose={() => handleLobSelect({ value: lobId })}
                                      className="selected-tag"
                                    >
                                      {lob?.label}
                                    </Tag>
                                  );
                                })}
                              </div>
                              <Input
                                placeholder="Search business lines..."
                                value={lobFilter}
                                onChange={e => setLobFilter(e.target.value)}
                                className="search-input"
                              />
                              <div className="suggestions-list">
                                {filteredLobs.map(lob => (
                                  <div
                                    key={lob.value}
                                    className={`suggestion-item ${selectedLobs.includes(lob.value) ? 'selected' : ''}`}
                                    onClick={() => handleLobSelect(lob)}
                                  >
                                    {lob.label}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24} style={{marginBottom: '40px'}}>
                      <Form.Item
                        name="general_info"
                        label="General Information"
                        initialValue=""
                      >
                        <ReactQuill 
                          theme="snow" 
                          modules={modules}
                          formats={formats}
                          placeholder="Additional company information"
                          style={{ height: '200px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  

                  


                  <Row gutter={16}>
                    <Col span={24} style={{marginBottom: '40px'}}>
                      <Form.Item
                        name="limits_of_coverage"
                        label="Limits of Coverage"
                        initialValue=""
                      >
                        <ReactQuill 
                          theme="snow" 
                          modules={modules}
                          formats={formats}
                          placeholder="Limits of Coverage"
                          style={{ height: '200px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24} style={{marginBottom: '10px'}}>
                      <Form.Item
                        name="underwriting"
                        label="Underwriting"
                        initialValue=""
                      >
                        <ReactQuill 
                          theme="snow" 
                          modules={modules}
                          formats={formats}
                          placeholder="Underwriting"
                          style={{ height: '200px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                </div>

                <div className="form-actions">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    size="large"
                  >
                    {id ? 'Update Company' : 'Create Company'}
                  </Button>
                  {id && (
                    <Button 
                      onClick={handlePreview}
                      size="large"
                      className="preview-button"
                    >
                      Preview Company
                    </Button>
                  )}
                  {/* <Button 
                    onClick={() => navigate('/admin/companies')}
                    size="large"
                    className="cancel-button"
                  >
                    Cancel
                  </Button> */}
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default AddCompanyPage;
